import React, { FC, MouseEventHandler, useMemo, useRef, useState } from "react"
import { graphql } from "gatsby"

import { PostBookTemplateQueryQuery } from "@graphqlTypes"

import {
  generateConditionalClassName,
} from "@utils"

// Shared Components
import SocialShare from "@components/SocialShare/SocialShare"

// Posts components
import PostsNav from "@templates/posts/components/PostsNav/PostsNav"

// Post components
import PostContent from "../components/PostContent/PostContent"
import PostFooter, { POST_FOOTER_STYLES } from "../components/PostFooter/PostFooter"
import PostHead from "../components/PostHead/PostHead"

// PostBook components
import PostBookFooterNav from "./components/PostBookFooterNav/PostBookFooterNav"
import PostBookHero from "./components/PostBookHero/PostBookHero"
import PostBookNav from "./components/PostBookNav/PostBookNav"
import PostBookNavMobileToggle from "./components/PostBookNavMobileToggle/PostBookNavMobileToggle"

// Styled components
import { POST_BOOK_THEMES, POST_BOOK_THEME_STATE, PostBookDIV, PostBookARTICLE } from "./PostBook.styles"

export { POST_BOOK_THEMES, POST_BOOK_THEME_STATE }

export const Head = PostHead

const PostBook: FC<{ data: PostBookTemplateQueryQuery }> = ({
  data: {
    contentfulPost,
    site,
  },
}) => {
  if (!contentfulPost || !contentfulPost.book) return null

  const {
    authors,
    content,
    dateModified,
    extendedImage,
    introduction,
    permalink,
    title,
    updatedAt,
  } = contentfulPost

  const bookData = contentfulPost.book[0]
  const currentChapterIndex = useMemo(() => bookData?.posts?.findIndex(chapter => chapter && chapter.id === contentfulPost.id), [bookData])

  const elRef = useRef<HTMLDivElement>(null)

  const [isNavOpen, setIsNavOpen] = useState<boolean>(false)

  const handleClickMobileNav: MouseEventHandler = (e) => {
    if (e) e.preventDefault()
    setIsNavOpen(_isNavOpen => !_isNavOpen)
  }

  const handlePageClick = () => {
    if (isNavOpen) setIsNavOpen(false)
  }

  const handleHideMobileNav = () => {
    setIsNavOpen(false)
  }

  return (
    <PostBookARTICLE
      className={generateConditionalClassName({
        [`theme--${bookData?.themeColor}`]: !!bookData?.themeColor,
        "is-nav-open": isNavOpen,
      })}
      onClick={handlePageClick}
      ref={elRef}
    >
      <PostsNav currentCategorySlug={"resources"} />
      <div>
        <div className="progress-bar">
          <div className="progress-bar--indicator" />
        </div>
        <div className="PostBook--nav-toggle-wrapper">
          <PostBookNavMobileToggle
            isNavOpen={isNavOpen}
            onClick={handleClickMobileNav}
            onHide={handleHideMobileNav}
            parentRef={elRef}
          />
        </div>
        <PostBookDIV>
          <PostBookHero
            authors={authors}
            book={bookData}
            date={dateModified || updatedAt}
            image={extendedImage}
            introduction={introduction}
            permalink={permalink}
            title={title}
          />
          <div className="SocialShare--wrapper">
            <SocialShare
              title={title || ""}
              url={`https://www.helpscout.com${permalink}`}
            />
          </div>
          <div className="PostBook--content">
            <PostBookNav
              book={bookData}
              currentChapterIndex={currentChapterIndex}
            />
            <div className="PostBook--content-main">
              <PostBookNav
                book={bookData}
                currentChapterIndex={currentChapterIndex}
                isStatic
              />
              <PostContent
                authors={authors}
                content={content}
                fullUrl={`${site?.siteMetadata?.siteUrl}${permalink}`}
                hideArticleNav
                hideAuthors
                hideSocialShare
                title={title || ""}
              />
              <PostBookFooterNav
                currentChapterIndex={currentChapterIndex}
                posts={bookData?.posts}
                themeColor={bookData?.themeColor}
              />
            </div>
          </div>
        </PostBookDIV>
      </div>
      <PostFooter footerStyle={POST_FOOTER_STYLES.DEFAULT} />
    </PostBookARTICLE>
  )
}

export const pageQuery = graphql`
  fragment BookFragment on ContentfulBook {
    title
    posts {
      ...CardFragment
    }
    slug
    themeColor
  }

  fragment PostBookFragment on ContentfulPost {
    ...PostTemplatePostFragment
    book {
      ...BookFragment
    }
    introduction {
      raw
    }
    id
  }

  query PostBookTemplateQuery(
    $id: String!
  ) {
    site {
      siteMetadata {
        siteUrl
      }
    }
    contentfulPost(
      id: { eq: $id }
    ) {
      ...PostBookFragment
    }
  }
`

export default PostBook
